import { useSearchWithTypeCounts } from 'hooks/api/backend/search/useSearchWithTypeCounts';
import { Input, InputGroup, Button, HStack, Text } from '@chakra-ui/react';
import { NovaIcon } from 'components/ui/novaIcon';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { useAddSearchEvent } from 'hooks/api/backend/search/search';
import {
  ChangeEvent,
  FormEvent,
  forwardRef,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CloseIcon, SearchIcon } from 'assets/icons/v2';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';

type SearchInputProps = {
  onFocus: () => void;
  onBlur: () => void;
  onCancelClick: () => void;
  onSubmit: () => void;
  showCancelButton: boolean;
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    { onFocus, onBlur, onCancelClick, onSubmit, showCancelButton }: SearchInputProps,
    ref
  ): ReactElement => {
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const { query, refine, clear } = useSearchWithTypeCounts();
    const searchEvent = useAddSearchEvent();

    // Initialize with URL query parameter if on search results page
    const urlQuery = searchParams.get('q') || '';
    const initialValue = pathname === ProtectedRoutes.SEARCH ? urlQuery : query;
    const [inputValue, setInputValue] = useState<string>(initialValue);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const debounceDelayInMs = 100; // debounce call delay
    const showClearButton = inputValue.length !== 0;

    // Keep the input in sync when the committed URL query changes (submit / back / forward).
    // Typing alone does not change `urlQuery`, so this does not fight local edits.
    useEffect(() => {
      if (pathname === ProtectedRoutes.SEARCH) {
        setInputValue(urlQuery);
      } else {
        setInputValue('');
      }
    }, [pathname, urlQuery]);

    const handleSubmit = async (
      e: FormEvent<HTMLFormElement> | FormEvent<HTMLButtonElement>
    ): Promise<void> => {
      e.preventDefault();
      // Update search history in backend
      if (inputValue.length > 0) {
        // Braze 'search_query_commited' event is fired from useAddSearchEvent
        // (addSearchEvent.ts) inside this mutation - don't duplicate it here.
        await searchEvent.mutateAsync(inputValue);
      }

      await refine(inputValue);
      onSubmit();
    };

    const handleDebouncedChange = (e: ChangeEvent<HTMLInputElement>): void => {
      e.preventDefault();
      const { value } = e.currentTarget;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(async () => {
        await refine(value);
      }, debounceDelayInMs);

      setInputValue(value);
    };

    const handleCrossClick = (): void => {
      setInputValue('');
      clear();
    };

    const handleFocus = async (): Promise<void> => {
      await refine(inputValue);
      onFocus();
    };

    const handleCancelClick = (): void => {
      setInputValue('');
      clear();
      onCancelClick();
    };

    return (
      <form autoComplete="off" onSubmit={handleSubmit}>
        <HStack gap="4">
          <InputGroup
            startElement={
              <NovaIcon icon={SearchIcon} size="s" color="icon.quaternary" aria-hidden />
            }
            endElement={
              showClearButton ? (
                <NovaIconButton
                  aria-label="Sucheingabe löschen"
                  variant="primary"
                  size="s"
                  onClick={handleCrossClick}
                  onMouseDown={handleCrossClick}
                  icon={CloseIcon}
                  iconProps={{ color: 'icon.quaternary' }}
                />
              ) : undefined
            }
          >
            <Input
              ref={ref}
              bg="background.primary"
              px="4"
              py="2"
              border="default"
              borderColor="border.accent"
              borderRadius="10px"
              placeholder="Fachbegriff, Schlagwort, Medikament, Pflegemaßnahme"
              value={inputValue}
              onChange={handleDebouncedChange}
              onFocus={handleFocus}
              onBlur={onBlur}
              _placeholder={{ fontSize: '14px', fontWeight: '300px', color: 'text.quaternary' }}
              _focus={{
                borderColor: 'border.accent',
                borderRadius: { md: '10px 10px 0 0' },
              }}
              // Limit should be below 512 characters as we hit instantsearch API limits otherwise
              maxLength={400}
            />
          </InputGroup>

          {showCancelButton && (
            <Button
              bg="transparent"
              _hover={{
                background: 'transparent',
                cursor: 'pointer',
              }}
              variant="tertiary"
              p="0"
              my="0"
              color={{ base: 'text.onAccent', lg: 'text.primary' }}
              onClick={handleCancelClick}
              // only tmp solution, because of mobile touch behavior
              onMouseDown={handleCancelClick}
            >
              <Text textStyle="body.s.medium">Abbrechen</Text>
            </Button>
          )}
        </HStack>
      </form>
    );
  }
);
